<template>
    <v-container class="font">
        <v-row justify="center">
            <v-col v-if="intro" cols="12" sm="8" md="8" lg="6">
                <div class="stage-title">{{stage.Name && stage.Name.toUpperCase()}}</div>
                <div class="bold">For this section you will require a webcam on your computer, or to log in on your mobile phone with a camera to complete this section.</div>
                <br />
                <div class="bold">PLEASE RECORD THIS IN A QUIET PLACE WITHOUT ANY BACKGROUND NOISE</div>
                <br />
                <div v-if="!IsPrimary">This section is an online interview and allows us to hear directly from you!</div>
                <div v-if="IsPrimary">This section is an online interview and allows us to hear <span style="font-weight: bold;text-decoration: underline;">directly from you and your child!</span></div>
                <br />
                <div class="underline">What happens now:</div>
                <br />
                <div><span class="bold">Practice</span> – Before we start the interview, we will do a practice. This practice interview will not count toward your application. You may do as many practice questions as you like. </div>
                <br />
                <div><span class="bold">Process</span> – A question will appear on your screen and you will have 20 seconds to think about the answer. You will then speak to camera to record your answer for a maximum of two minutes. You cannot pause the recording or redo your answer.</div>
                <br />
                <div><span class="bold">3 Questions</span> – This process will repeat for the second and third question, after which your responses will be saved and this section will be locked.</div>
                <br />
                <div class="bold">Let’s start with a practice question!</div>
                <br />
                <v-btn class="button btn-font" @click="preDummyQuestion" color="#ea7f1d" large>LET'S GO</v-btn>
            </v-col>
            <v-col v-else cols="12" sm="12" md="10" lg="10">
                <div class="steps-container">
                    <v-row class="component-style" justify="space-around">
                        <v-col cols="3" :class="[key%2 === 0 ? 'margin-adjust':'no-space']" v-for="(item, key) in questionSteps" :key="key">
                            <div v-if="key%2 !== 0">
                                <div :class="['key', 'oval', 'centre-align', ((key - 1)/2 + 1) < activeStep ? 'oval-burnt-red' : ((key - 1)/2 + 1) === activeStep ? 'oval-orange' : 'oval-default']">
                                    <span class="centre-align">{{key==='1' ? 'P' : ((key - 1)/2)}}</span>
                                </div>
                                <v-spacer class="row-space"></v-spacer>
                                <div :class="['details', 'centre-align', ((key - 1)/2 + 1) === activeStep ? 'details-bold' : 'details-normal']">{{ item }}</div>
                            </div>
                            <div v-else class="link centre-align">
                                <div class="rectangle centre-align"></div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <div v-if="!postInterview">
                    <v-row justify="center">
                        <v-col cols="12" sm="5">
                            <div class="stage-title title-left">{{stage.Name && stage.Name.toUpperCase()}}</div>
                            <div v-if="isDummyQuestion">
                                <div class="sub-title">PRACTICE QUESTION</div>
                                <div v-if="!isDummyQuestionDone">
                                    <div v-if="preInterview">
                                        <div>This is a practice question to get you comfortable with the format and to check your mic and camera. </div>
                                        <br />
                                        <ul>
                                            <li>You have 20 seconds to think before the recording starts, and then up to 2 minutes to give your answer. </li>
                                            <li>If you finish early, click <span class="bold">I’m Done</span>.</li>
                                            <li>When the recording begins, please read the question out loud before you answer the question.</li>
                                            <li>Once finished, make sure you playback your response to check everything is working. Note: playback is only available on the practice questions.</li>
                                        </ul>
                                        <br />
                                        <v-btn class="btn-white btn-font" @click="startDummyQuestion" color="#ea7f1d" large>REVEAL QUESTION & START CLOCK</v-btn>
                                    </div>
                                    <div v-else>
                                        <div class="question-title">{{dummyQuestions[dummyIndex] && dummyQuestions[dummyIndex].Question_Text__c}}</div>
                                        <br />
                                        <div> This 20 seconds is for you to read the question and have a short think about how to answer it. </div>
                                    </div>
                                </div>
                                <div v-else>
                                    <div class="bold">Take a moment to play back your practice recording to check that your camera and mic are working. Playback is only available for the practice questions.</div>
                                    <br />
                                    <div>Are you ready to start your interview? If not, that's ok. Click on <span class="bold">Another Practice</span> to do another practice.</div>
                                    <br />
                                    <div>If you are ready, click <span class="bold">Start Interview</span> to begin.</div>
                                    <br />
                                    <div class="bold">Warning: If you press start you have to continue until you finish.</div>
                                    <v-btn class="full-length dummy-btn btn-font" @click="preDummyQuestion" outlined color="#4d4d4d" large>ANOTHER PRACTICE</v-btn>
                                    <v-btn class="btn-white full-length dummy-btn btn-font" :disabled="!questions.length" @click="preRealQuestion" color="#ea7f1d" large>{{questions.length ? 'START INTERVIEW' : 'LOADING INTERVIEW'}}</v-btn>
                                </div>
                            </div>
                            <div v-else>
                                <div v-if="preInterview">
                                    <div class="sub-title">QUESTION {{nextIndex+1}} OF {{questions.length}}</div>
                                    <br />
                                    <div>When the recording begins, please read the question out loud before you answer the question.</div>
                                    <br />
                                    <v-btn class="btn-white btn-font" @click="startRealQuestion" color="#ea7f1d" large>REVEAL QUESTION & START CLOCK</v-btn>
                                </div>
                                <div v-else>
                                    <div class="sub-title">QUESTION {{nextIndex+1}} OF {{questions.length}}</div>
                                    <br />
                                    <div class="question-title">{{currentQuestion.Question_Text__c}}</div>
                                    <br />
                                    <div>When the recording begins, please read the question out loud before you answer the question.</div>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="7">
                            <div class="video-container">
                                <div class="interview-bar">
                                    <div class="interview-left">
                                        <div v-if="isRecording" class="rec-container">
                                            <span class="rec-icon">⦿ REC</span>
                                            <span class="rec-number">{{formatCountdown(recordingSeconds)}}</span>
                                        </div>
                                        <div v-else>{{formatCountdown(waitingSeconds)}}</div>
                                    </div>
                                    <div class="interview-middle">
                                        <div style="font-size:12px;">{{isRecording ? 'RESPONSE TIME REMAINING' : 'RECORDING BEGINS'}}</div>
                                        <v-progress-linear
                                            class="progress-bar"
                                            :color="isRecording ? '#ea7f1d' : '#6e6e6e'"
                                            background-color="#ffffff"
                                            rounded
                                            :value="progress"
                                            height="12"
                                        ></v-progress-linear>
                                    </div>
                                    <div class="interview-right" :style="!isRecording?'background-color:#d5d5d5;':''"  @click="stopRecording">I’M DONE</div>
                                </div>
                                <video id="interview" playsinline class="video-js vjs-default-skin interview"></video>
                            </div>
                        </v-col>
                    </v-row>
                </div>
                <div v-else>
                    <v-row justify="center">
                        <v-col cols="12" sm="8" md="8" lg="6">
                            <div class="stage-title">{{stage.Name && stage.Name.toUpperCase()}}</div>
                            <div class="post-text">
                                <div v-if="completed">
                                    <div>Congratulations!</div>
                                    <br />
                                    <div>You have completed the interview section of your application. </div>
                                    <br />
                                    <div>This section is now final and you are not able to edit your interview. </div>
                                    <br />
                                    <div>We can't wait to watch your interview and hopefully meet you in person!</div>
                                    <br />
                                    <v-row justify="center">
                                        <v-col cols="5">
                                            <v-btn class="btn-white full-length btn-font" @click="goBack" color="#ea7f1d" large>FINISH INTERVIEW</v-btn>
                                        </v-col>
                                    </v-row>
                                </div>
                                <div v-else>
                                    <div>Well done!</div>
                                    <br />
                                    <div>{{currentQuestion['Answer__c.Answer__c'] ? `Are you ready for your ${textForNextQuestion} question?` : 'Uploading video, please wait.'}}</div>
                                    <br />
                                    <v-row justify="center">
                                        <v-col cols="5">
                                            <v-btn class="btn-white full-length btn-font" @click="preRealQuestion" :disabled="!currentQuestion['Answer__c.Answer__c']" color="#ea7f1d" large>{{currentQuestion['Answer__c.Answer__c'] ? `LET'S GO` : 'Saving...'}}</v-btn>
                                        </v-col>
                                    </v-row>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="preInterview && isDummyQuestion && !isDummyQuestionDone" justify="center">
            <v-col cols="12" sm="12" md="10" lg="10">
                <!-- <v-row> -->
                    <div class="tips-title">QUICK TIPS</div>
                <!-- </v-row> -->
                <div class="tips-body">
                    <div class="tips-part">
                        <img src="/interview_tip_1.svg" />
                        <div>Are your mic and camera on? You may need to allow permissions to use these.</div>
                    </div>
                    <div class="tips-part">
                        <img src="/interview_tip_2.svg" />
                        <div>Are you looking directly at the camera with your head and shoulders centred?</div>
                    </div>
                    <div class="tips-part">
                        <img src="/interview_tip_3.svg" />
                        <div>Are you in a quiet space with no surrounding noise?</div>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-dialog
            v-model="browserNotSupported"
            hide-overlayd
            persistent
            max-width="600"
            width="60%"
        >
            <div class="browser-not-supported">
                <div class="browser-text">Sorry, your browser doesn't support video recording, please open this page on the latest Google Chrome or Microsoft Edge.</div>
                <v-btn class="btn-white full-length btn-font" @click="goBack" color="#ea7f1d" large>BACK</v-btn>
            </div>
        </v-dialog>
    </v-container>
</template>

<script>
import 'video.js/dist/video-js.css'
import 'videojs-record/dist/css/videojs.record.css'
// import axios from 'axios'
import {upload} from '../../client.js';

import RecordRTC from 'recordrtc'

import videojs from 'video.js'
/* eslint-disable no-unused-vars */
import Record from 'videojs-record/'
/* eslint-enable no-unused-vars */

import {SequentialQueue} from '../../utils/sequentialQueue'
import Client from '../../client.js';
let recordingTimer;
let waitingTimer;

const WAITING = 20;
const RECORDING = 120;

export default {
    // components: { InterviewLayout },
    props: {
        stage_key: String,
        sort: [String, Number],
        application_id: [String, Number]
    },
    computed: {
        stage() {
            const stages = this.$store.getters['stage/stages'];
            if(stages.length === 0) return {};
            return stages.find(s => s.Id === this.stage_key);
        },
        questions() {
            const questionsWithAnswer = this.$store.getters['stage/questionsWithAnswer'];
            if(!questionsWithAnswer[this.stage_key]) return [];
            return questionsWithAnswer[this.stage_key].filter(q => q['Interview_Question_Type__c'] == 'Actual');
        },
        dummyQuestions() {
            const questionsWithAnswer = this.$store.getters['stage/questionsWithAnswer'];
            if(!questionsWithAnswer[this.stage_key]) return [];
            return questionsWithAnswer[this.stage_key].filter(q => q['Interview_Question_Type__c'] == 'Practice');
        },
        options() {
            const option = {
                controls: false,
                autoplay: false,
                fluid: true,
                loop: true,
                controlBar: {
                    volumePanel: false,
                    pipToggle: false,
                    recordToggle: false,
                    fullscreenToggle: false,
                },
                plugins: {
                    // configure videojs-record plugin
                    record: {
                        audio: true,
                        video: true,
                        maxLength: 10000,
                        debug: false,
                        pip: false,
                        videoMimeType: 'video/mp4',
                        timeSlice: 2000
                    }
                }
            }
            if(this.isDummyQuestion) {
                option.controls = true;
            }
            return option;
        },
        nextIndex() {
            if(this.questions.length === 0) return null;
            return this.questions.findIndex(q => !q['Answer__c.Answer__c']);
        },
        completed() {
            if(this.questions.length === 0) return false;
            return this.questions.every(q => q['Answer__c.Answer__c']);
        },
        questionSteps() {
            if(this.questions.length === 0) return {};
            const steps = {
                1: "Practice",
            }
            this.questions.forEach((item, index) => {
                const len = Object.keys(steps).length;
                steps[len+1] = '';
                steps[len+2] = `Question ${index+1}`
            })
            return steps;
        },
        activeStep() {
            if(this.isDummyQuestion) return 1;
            if(this.nextIndex === -1) {
                const max = Object.keys(this.questionSteps).length;
                return (max - 1)/2 + 2;
            }
            return this.postInterview ? this.nextIndex + 1 : this.nextIndex + 2;
        },
        progress() {
            return this.isRecording ? this.recordingSeconds / RECORDING * 100 : this.waitingSeconds / WAITING * 100
        },
        textForNextQuestion() {
            if(this.nextIndex === 1) {
                return 'second'
            }
            if(this.nextIndex === 2) {
                return 'third and final'
            }
            return 'next'
        }
    },
    data: function () {
        return {
            intro: true,
            isDummyQuestion: true,
            isRecording: false,
            postInterview: false,
            waitingSeconds: WAITING,
            recordingSeconds: RECORDING,
            player: null,
            currentQuestion: {},
            isDummyQuestionDone: false,
            uploading: {},
            dummyIndex: 0,
            browserNotSupported: false,
            preInterview: false,
            IsPrimary: false,
        }
    },
    async beforeMount() {
        let client = Client();
        await client.get('me/profile').then(res => {
            if(res.data.school_level.toLowerCase() == 'primary'){
                this.IsPrimary = true;
            }
        }, () => {})      
    },
    mounted() {
        this.checkBrowserSupport();
        console.log('stage');
        console.log(this.stage);
    },
    beforeDestroy() {
        this.destroyPlayer();
    },
    methods: {
        formatCountdown(secs) {
            function pad(num) {
                return ("0"+num).slice(-2);
            }
            function mmss(_secs) {
                const minutes = Math.floor(_secs / 60);
                const seconds = _secs%60;
                return `${minutes}:${pad(seconds)}`;
            }
            return mmss(secs);
        },
        checkBrowserSupport() {
            if(!window.MediaRecorder) {
                this.browserNotSupported = true;
            }
        },
        showUploading(id) {
            this.uploading[id] = true;
        },
        hideUploading(id) {
            this.uploading[id] = false;
        },
        genRandomInt(min, max) {
            return Math.floor(Math.random() * (max-min+1)) + min;
        },
        preDummyQuestion() {
            this.waitingSeconds = WAITING;
            this.resetPlayer();
            this.dummyIndex = this.genRandomInt(0, 2);
            this.intro = false;
            this.isDummyQuestion = true;
            this.isRecording = false;
            this.postInterview = false;
            this.isDummyQuestionDone = false;
            this.preInterview = true;
        },
        startDummyQuestion() {
            this.preInterview = false;
            this.startInterviewProcess();
        },
        startInterviewProcess() {
            this.waitingSeconds = WAITING;
            this.recordingSeconds = RECORDING;
            waitingTimer = setInterval(() => {
                if(this.waitingSeconds > 0) {
                    this.waitingSeconds--;
                }
                if(this.waitingSeconds === 0) {
                    this.startRecording();
                }
            }, 1000)
            setTimeout(() => {
                this.turnOnDevice();
            }, 0);
        },
        stopRecording() {
            if(this.isRecording) {
                this.player && this.player.record().stopDevice();
            }
        },
        preRealQuestion() {
            this.waitingSeconds = WAITING;
            this.resetPlayer();
            if(this.nextIndex === -1) {
                this.isDummyQuestion = false;
                this.postInterview = true;
                return;
            }
            this.preInterview = true;
            this.isDummyQuestion = false;
            this.isDummyQuestionDone = false;
            this.postInterview = false;
            this.currentQuestion = this.questions[this.nextIndex];
        },
        startRealQuestion() {
            this.preInterview = false;
            this.startInterviewProcess();
        },
        destroyPlayer() {
            if(this.player) {
                this.player.record() && this.player.record().destroy();
                this.player = null;
            }
        },
        resetPlayer() {
            if(this.player) {
                this.player.record() && this.player.record().reset() && this.player.record().stopDevice();
            }
        },
        gotoPostInterview(queue) {
            this.postInterview = true;
            this.saveFinalVideo(queue);
        },
        saveFinalVideo(queue) {
            // console.log(queue, upload)
            // this.player.record().saveAs({'video': this.player.recordedData.name});
            const task = async () => {
                const formData = new FormData();
                formData.append('final', 1);
                formData.append('filename', `${this.application_id}-${this.currentQuestion.Id}`);
                formData.append('answerId', this.currentQuestion['Answer__c.Id']);
                await upload(`/applications/${this.application_id}/interview/${this.currentQuestion.Id}`, formData);
                // this.hideUploading(id);
                // this.$store.commit('loading/push', {id});
            }
            queue.enqueue(task, {over: true});
            // this.showUploading(id);
            // this.$store.commit('loading/push', {id, message: 'Interview is uploading, please do not close or refresh the page.'});
            this.$store.commit('stage/updateAnswer', {stageId: this.stage_key, questionId: this.currentQuestion.Id, answer: 'placeholder'});
            this.currentQuestion['Answer__c.Answer__c'] = 'placeholder';
            // console.log('current question: ', this.currentQuestion)
            this.destroyPlayer();
        },
        saveVideoChunk(queue, filename) {
            // console.log(queue, upload)
            const binaryData = this.player.recordedData[this.player.recordedData.length - 1];
            const task = async () => {
                const formData = new FormData();
                if(binaryData) {
                    formData.append('file', binaryData);
                    formData.append('filename', filename);
                } else {
                    formData.append('start', 1);
                    formData.append('filename', `${this.application_id}-${this.currentQuestion.Id}`);
                    this.$store.commit('stage/updateStageStatus', {stageId: this.stage_key, status: 'In Progress'})
                    if(this.nextIndex === this.questions.length - 1 || this.questions.filter(q => !q['Answer__c.Answer__c']).length <= 1) {
                        formData.append('lastOne', 1);
                        this.$store.commit('stage/updateStageStatus', {stageId: this.stage_key, status: 'Completed'})
                    }
                }
                formData.append('answerId', this.currentQuestion['Answer__c.Id']);
                await upload(`/applications/${this.application_id}/interview/${this.currentQuestion.Id}?stageId=${this.stage_key}`, formData);
            }
            queue.enqueue(task, {async: true});
        },
        goBack() {
            // this.$store.dispatch('stage/fetchStages', {appId: this.application_id});
            this.$router.push({name: 'application.dashboard'})
        },
        /**
         * @TODO DO NOT CHANGE PLAYER CALLBACK BINDINGS, YOU WILL BREAK IT AND
         * IT WILL TAKE HOURS TO DEBUG..............
         */
        startRecording() {
            clearInterval(waitingTimer);
            if (this.player) {
                // this.player.record().loadOptions(this.options);
                this.player.record().start()
                // this.renderVideo = true;
            } else {
                this.initPlayer();
                setTimeout(() => {
                    this.player.record().start()
                }, 1000);
            }
            // this.player.record().getDevice();
        },
        turnOnDevice() {
            this.initPlayer();
            this.player.record().getDevice();
        },
        initPlayer() {
            if(this.player) {
                this.resetPlayer();
                return;
            }
            console.log('init player')
            const queue = new SequentialQueue();
            this.player = videojs('#interview', this.options, () => {
                var msg = 'Using video.js ' + videojs.VERSION +
                    ' with videojs-record ' + videojs.getPluginVersion('record') +
                    ' and recordrtc ' + RecordRTC.version;
                videojs.log(msg);
            });

            // let deviceReady = function() {
            //     this.player.record().start();
            // };

            this.player.on('deviceReady', () => {
                console.log('device ready');
            });

            let startRecord = function() {
                clearInterval(waitingTimer);
                this.isRecording = true;
                console.log('--------------------------start--------------------------')
                let timeoutFunction = function() {
                    if(this.recordingSeconds-- === 0) {
                        this.player.record().stopDevice();
                    }
                };
                
                recordingTimer = setInterval(timeoutFunction.bind(this), 1000);
            };

            this.player.on('startRecord', startRecord.bind(this));

            let finishRecord = function() {
                clearInterval(recordingTimer);
                console.log('--------------------------finish--------------------------')
                console.log(this.player.recordedData.name)
                this.isRecording = false;
                if(this.isDummyQuestion) {
                    this.isDummyQuestionDone = true;
                    return;
                }
                this.gotoPostInterview(queue);
            };

            // user completed recording and stream is available
            this.player.on('finishRecord', finishRecord.bind(this));

            this.player.on('timestamp', () => {
                console.log('current timestamp: ', this.player.currentTimestamp);
                if(this.isDummyQuestion) return;
                this.saveVideoChunk(queue, `${this.application_id}-${this.currentQuestion.Id}-chunk-${this.player.currentTimestamp}.mp4`);
            });

            // error handling
            // this.player.on('error', (element, error) => {});

            this.player.on('deviceError', () => {});
        }
    }
}
</script>
<style lang="scss" scoped>
.rec-container {
    display: flex;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    height: 100%;
}
.rec-number {
    min-width: 50px;
}
.rec-icon {
    color: #ff0000;
    font-size: 12px;
    flex-basis: initial;
}
.question-title {
    font-family: Hind;
    font-size: 28px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.29;
    letter-spacing: normal;
}
.tips-part {
    display: flex;
    justify-content: center;
    flex-basis: 32%;
    >img {
        margin-right: 20px;
    }
}
.tips-body {
    display: flex;
    justify-content: space-between;
    font-family: Hind;
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.25;
    letter-spacing: normal;
}
.tips-title {
    font-family: Dosis;
    font-size: 24px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: 3.2px;
    margin-bottom: 20px;
}
.stage-title {
    text-align: center;
    font-size: 30px;
    font-weight: 600;
    font-family: $body-font-family;
    margin-bottom: 28px;
}
.title-left {
    text-align: left;
}
.font {
    font-family: $secondary-font;
}
.button {
    width: 100%;
    color: white;
    font-family: $btn-font;
}
.bold {
    font-weight: 600;
}
.underline {
    text-decoration: underline;
}
.sub-title {
    font-family: $body-font-family;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: 3.2px;
    margin-bottom: 30px;
}
.interview {
    width: 100%;
    height: 300px;
}
.interview-bar {
    display: flex;
    align-items: center;
    height: 57px;
}
.interview-left {
    background-color: #d5d5d5;
    font-family: $btn-font;
    font-size: 24px;
    font-weight: 600;
    line-height: 0.67;
    letter-spacing: 1.5px;
    text-align: center;
    color: #626262;
    height: 100%;
    flex-basis: 25%;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.interview-middle {
    background-color: #eaeaea;
    font-family: $btn-font;
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.88px;
    text-align: center;
    color: black;
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 4px 8px;
    .v-progress-linear {
        transition-duration: 1s;
    }
}
.interview-right {
    background-color: #ea7f1d;
    font-family: $btn-font;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 1px;
    text-align: center;
    color: white;
    height: 100%;
    flex-basis: 30%;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}
.btn-white {
    color: white;
}
.post-text {
    text-align: center;
}
.full-length {
    width: 100%;
    font-family: $btn-font;
}
.steps-container {
    display: flex;
    justify-content: center;
    margin-bottom: 40px;
}
.progress-bar {
    margin-top: 8px;
}

.component-style
{
    max-width: 721px
}

.no-space
{
    padding: 0;
    margin:0;
}

.margin-adjust
{
    padding: 0;
    margin-left: -250px;
    margin-right: -250px;
}

@media (max-width: 425px) {
    .margin-adjust
{
    display: none;
}
}

.link
{
      padding-top: 10px
}

.key {
  width: 22px;
  height: 23px;
  font-family: Hind;
  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  text-align: center;
  color: #000000
}

.oval {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}

.oval-default
{
    background-color: #c2c2c2;
    color: #000000
}

.oval-orange
{
    background-color: #ea7f1d;
    color: #ffffff
}

.oval-burnt-red
{
    background-color: #b11116;
    color: #ffffff
}

.details {
  width: 110px;
  height: 23px;
  font-family: Hind;
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #000000;
}

.details-bold
{
      font-weight: bold
}

.details-normal
{
      font-weight: normal
}

.row-space{
    height: 6px;
}

.rectangle {
  width: 65px;
  height: 2px;
  border-radius: 3px;
  background-color: #c2c2c2;
}

.centre-align
{
    margin: auto
}

.browser-not-supported {
    font-family: $secondary-font;
    background-color: white;
    padding: 20px;
}
.browser-text {
    margin-bottom: 20px;
}
.dummy-btn {
    margin-top: 30px;
}
</style>
